<!-- File: frontend/src/components/SelectCharts.vue -->

<template>
  <div class="container">
    <!-- Sidebar for selectors -->
    <aside :class="['sidebar', { open: sidebarOpen }]">
      <button class="sidebar-toggle" @click="sidebarOpen = !sidebarOpen" aria-label="Toggle filters">
        <span v-if="sidebarOpen">&#10005;</span>
        <span v-else>&#9776;</span>
      </button>
      <div v-show="sidebarOpen" class="sidebar-content">
        <!-- All selectors grouped here -->
        <div class="selectors">
          <!-- Location Select -->
          <div class="selector-item">
            <label for="location-select">Location:</label>
            <select id="location-select" v-model="selectedLocation"
              @change="() => { updateSensorOptions(); fetchData(); }">
              <option v-for="location in locations" :key="location" :value="location">
                {{ location }}
              </option>
            </select>
          </div>

          <!-- Sensor Type Select -->
          <div class="selector-item">
            <label for="sensor-select">Sensor Type:</label>
            <select id="sensor-select" v-model="selectedSensor" @change="fetchData">
              <option v-for="sensor in sensors" :key="sensor" :value="sensor">
                {{ sensor }}
              </option>
            </select>
          </div>

          <!-- Indoor Sensor Select -->
          <div class="selector-item">
            <label for="indoor-sensor-select">Indoor Sensor:</label>
            <select id="indoor-sensor-select" v-model="selectedIndoorSensor" @change="fetchData">
              <option value="None">None</option>
              <option v-for="indoor in indoorSensorOptions" :key="indoor" :value="indoor">
                {{ indoor }}
              </option>
            </select>
          </div>

          <!-- Outdoor Sensor Select -->
          <div class="selector-item">
            <label for="outdoor-sensor-select">Outdoor Sensor:</label>
            <select id="outdoor-sensor-select" v-model="selectedOutdoorSensor" @change="fetchData">
              <option value="None">None</option>
              <option v-for="outdoor in outdoorSensorOptions" :key="outdoor" :value="outdoor">
                {{ outdoor }}
              </option>
            </select>
          </div>

          <!-- Delta checkbox -->
          <div class="checkbox-item">
            <input type="checkbox" id="delta-checkbox" v-model="showDelta" @change="fetchData" />
            <label for="delta-checkbox">Show Delta</label>
          </div>

          <!-- Show all interpolated points checkbox -->
          <div v-if="showDelta" class="checkbox-item">
            <input type="checkbox" id="show-all-interpolated" v-model="showAllInterpolated" @change="fetchData" />
            <label for="show-all-interpolated">Show All Interpolated Points</label>
          </div>

          <!-- Gap period threshold selection -->
          <div v-if="showDelta" class="selector-item">
            <label for="gap-threshold">Big Gap Threshold (minutes):</label>
            <select id="gap-threshold" v-model="gapThreshold" @change="fetchData">
              <option v-for="threshold in gapThresholdOptions" :key="threshold" :value="threshold">
                {{ threshold }}
              </option>
            </select>
          </div>

          <!-- Range selection -->
          <div class="selector-item">
            <label for="range-select">Time Range (days):</label>
            <select id="range-select" v-model="selectedRangeDays" @change="fetchData">
              <option v-for="d in possibleRanges" :key="d" :value="d">{{ d }} days</option>
            </select>
          </div>
        </div>
      </div>
    </aside>
    <div :class="['main-content', { 'expanded': !sidebarOpen }]">
      <!-- Loading spinner -->
      <div v-if="loading" class="loading-spinner">
        <div class="spinner"></div>
      </div>
      <!-- Display gap warning if present -->
      <div v-if="gapWarning" class="warning">{{ gapWarning }}</div>
      <!-- Chart header section -->
      <div class="chart-header">
        <div class="chart-actions">
          <button @click="exportChart">Export</button>
          <button @click="fullscreenChart">Fullscreen</button>
        </div>
      </div>
      <div id="chart"></div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import * as echarts from 'echarts';
import axios from 'axios';
import apiClient from '@/services/api.js';
// Add composable import
import { useChart } from '@/composables/useChart.js';

export default {
  props: {
    initialLocation: {
      type: String,
      default: 'Kendeda'
    },
    initialIndoorSensor: {
      type: String,
      default: 'None'
    },
    initialOutdoorSensor: {
      type: String,
      default: 'None'
    }
  },
  setup(props) {
    const locationSensorMap = ref({});
    const locations = ref([]);
    const sensors = ref(['co2', 'humidity', 'temperature', 'pressure']);
    const indoorSensorOptions = ref([]);
    const outdoorSensorOptions = ref([]);
    const selectedLocation = ref(props.initialLocation);
    const selectedSensor = ref('co2');
    const selectedIndoorSensor = ref(props.initialIndoorSensor);
    const selectedOutdoorSensor = ref(props.initialOutdoorSensor);
    const showDelta = ref(true);
    const showAllInterpolated = ref(false); // New state for toggling all interpolated points
    const possibleRanges = ref([1, 3, 7, 14, 30, 60]);
    const selectedRangeDays = ref(7);
    const gapWarning = ref('');
    const gapThreshold = ref(30); // New state for gap threshold, default 30 minutes
    const gapThresholdOptions = ref([10, 20, 30, 60, 120, 240]); // Options for gap threshold
    const largeGapThreshold = ref(1440); // New state for large gap threshold, default 1 day (1440 minutes)
    const sidebarOpen = ref(window.innerWidth > 900); // Sidebar open by default on large screens
    const loading = ref(false);

    // Use composable for chart logic
    const { initChart, updateChart: setChartOption, getChartInstance } = useChart();

    // Helper to build chart title based on filters
    const getChartTitle = () => {
      let loc = selectedLocation.value;
      let sensor = selectedSensor.value.toUpperCase();
      let indoor = selectedIndoorSensor.value;
      let outdoor = selectedOutdoorSensor.value;
      let parts = [];
      if (showDelta.value) {
        if (indoor !== 'None' && outdoor !== 'None') {
          parts.push(`${loc} - ${sensor} (Delta: ${indoor} vs ${outdoor})`);
        } else if (indoor !== 'None') {
          parts.push(`${loc} - ${sensor} (Indoor: ${indoor})`);
        } else if (outdoor !== 'None') {
          parts.push(`${loc} - ${sensor} (Outdoor: ${outdoor})`);
        } else {
          parts.push(`${loc} - ${sensor}`);
        }
      } else {
        if (indoor !== 'None' && outdoor !== 'None') {
          parts.push(`${loc} - ${sensor} (Indoor: ${indoor}, Outdoor: ${outdoor})`);
        } else if (indoor !== 'None') {
          parts.push(`${loc} - ${sensor} (Indoor: ${indoor})`);
        } else if (outdoor !== 'None') {
          parts.push(`${loc} - ${sensor} (Outdoor: ${outdoor})`);
        } else {
          parts.push(`${loc} - ${sensor}`);
        }
      }
      return parts.join(' ');
    };

    // Helper for Y-axis label (could be improved with units per sensor)
    const getYAxisLabel = () => {
      switch (selectedSensor.value) {
        case 'co2': return 'Sensor Value (ppm)';
        case 'humidity': return 'Sensor Value (%)';
        case 'temperature': return 'Sensor Value (°C)';
        case 'pressure': return 'Sensor Value (hPa)';
        default: return 'Sensor Value (units)';
      }
    };

    const updateSensorOptions = () => {
      if (!selectedLocation.value || !locationSensorMap.value[selectedLocation.value]) {
        indoorSensorOptions.value = [];
        outdoorSensorOptions.value = [];
        return;
      }

      indoorSensorOptions.value = locationSensorMap.value[selectedLocation.value].indoor || [];
      outdoorSensorOptions.value = locationSensorMap.value[selectedLocation.value].outdoor || [];

      if (selectedLocation.value === 'East Point') {
        selectedIndoorSensor.value = indoorSensorOptions.value[0] || 'None';
        selectedOutdoorSensor.value = outdoorSensorOptions.value[0] || 'None';
        showDelta.value = false;
      } else {
        if (!indoorSensorOptions.value.includes(selectedIndoorSensor.value) && selectedIndoorSensor.value !== 'None') {
          selectedIndoorSensor.value = 'None';
        }
        if (!outdoorSensorOptions.value.includes(selectedOutdoorSensor.value) && selectedOutdoorSensor.value !== 'None') {
          selectedOutdoorSensor.value = 'None';
        }
        showDelta.value = true;
      }
    };

    // Replace chart = echarts.init(...) with composable
    const initChartWrapper = () => {
      initChart(document.getElementById('chart'));
    };

    // Watch filters and fetch data automatically
    watch(
      [
        selectedLocation,
        selectedSensor,
        selectedIndoorSensor,
        selectedOutdoorSensor,
        showDelta,
        showAllInterpolated,
        gapThreshold,
        selectedRangeDays
      ],
      () => {
        fetchData();
      }
    );

    // Fetch data and update chart
    const fetchData = async () => {
      loading.value = true;
      if (!selectedLocation.value) {
        loading.value = false;
        return;
      }

      let timestamps = null;
      let isInterpolatedIndoor = null;
      let isInterpolatedOutdoor = null;
      let gapMetadata = null;
      const series = [];
      gapWarning.value = '';

      const loc = selectedLocation.value;
      const sensorType = selectedSensor.value;
      const rangeStr = `${selectedRangeDays.value}d`;

      // Assign colors for each series
      const colorIndoor = '#667eea';
      const colorOutdoor = '#76b852';
      const colorDelta = '#e67e22';

      if (showDelta.value) {
        const defaultIndoor = indoorSensorOptions.value[0];
        const defaultOutdoor = outdoorSensorOptions.value[0];

        if (!defaultIndoor || !defaultOutdoor) {
          console.error('No sensors available to compute delta for this location.');
          return;
        }

        const indoorParam = selectedIndoorSensor.value === 'None' ? defaultIndoor : selectedIndoorSensor.value;
        const outdoorParam = selectedOutdoorSensor.value === 'None' ? defaultOutdoor : selectedOutdoorSensor.value;

        const apiUrl = `/api/delta/${loc}/${sensorType}?indoor_sensor=${indoorParam}&outdoor_sensor=${outdoorParam}&range=${rangeStr}`;
        try {
          const response = await apiClient.get(apiUrl);
          const { timestamps: newTimestamps, indoor_value, outdoor_value, values: delta_values, gap_metadata, is_interpolated_indoor, is_interpolated_outdoor } = response.data;
          timestamps = newTimestamps;
          isInterpolatedIndoor = is_interpolated_indoor;
          isInterpolatedOutdoor = is_interpolated_outdoor;
          gapMetadata = gap_metadata;

          // Debugging: Verify lengths
          console.log('Timestamps length:', timestamps.length);
          console.log('Indoor values length:', indoor_value.length);
          console.log('Outdoor values length:', outdoor_value.length);
          console.log('Delta values length:', delta_values.length);
          console.log('isInterpolatedIndoor length:', isInterpolatedIndoor.length);
          console.log('isInterpolatedOutdoor length:', isInterpolatedOutdoor.length);

          // Handle gap metadata
          if (gap_metadata && gap_metadata.gap_percentage > 10) {
            gapWarning.value = `Warning: ${gap_metadata.gap_percentage.toFixed(2)}% of the data is interpolated due to gaps.`;
          }

          if (selectedIndoorSensor.value !== 'None') {
            series.push({
              name: `${sensorType} indoor`,
              type: 'line',
              data: indoor_value,
              smooth: true,
              color: colorIndoor
            });
          }

          if (selectedOutdoorSensor.value !== 'None') {
            series.push({
              name: `${sensorType} outdoor`,
              type: 'line',
              data: outdoor_value,
              smooth: true,
              color: colorOutdoor
            });
          }

          series.push({
            name: `${sensorType} delta`,
            type: 'line',
            data: delta_values,
            smooth: true,
            color: colorDelta
          });
        } catch (error) {
          console.error('Error fetching delta data:', error);
        }
      } else {
        if (selectedIndoorSensor.value !== 'None') {
          const indoorUrl = `/api/data/${loc}/${sensorType}/indoor/${selectedIndoorSensor.value}?range=${rangeStr}`;
          try {
            const response = await apiClient.get(indoorUrl);
            const { timestamps: indoorTimestamps, values: indoorValues } = response.data;
            if (!timestamps) {
              timestamps = indoorTimestamps;
            }
            series.push({
              name: `${sensorType} indoor`,
              type: 'line',
              data: indoorValues,
              smooth: true,
              color: colorIndoor
            });
          } catch (error) {
            console.error('Error fetching indoor data:', error);
          }
        }

        if (selectedOutdoorSensor.value !== 'None') {
          const outdoorUrl = `/api/data/${loc}/${sensorType}/outdoor/${selectedOutdoorSensor.value}?range=${rangeStr}`;
          try {
            const response = await apiClient.get(outdoorUrl);
            const { timestamps: outdoorTimestamps, values: outdoorValues } = response.data;
            if (!timestamps) {
              timestamps = outdoorTimestamps;
            }
            series.push({
              name: `${sensorType} outdoor`,
              type: 'line',
              data: outdoorValues,
              smooth: true,
              color: colorOutdoor
            });
          } catch (error) {
            console.error('Error fetching outdoor data:', error);
          }
        }
      }

      await Promise.resolve(); // Ensure async for loading spinner
      updateChart(timestamps, series, isInterpolatedIndoor, isInterpolatedOutdoor, gapMetadata);
      loading.value = false;
    };

    // Replace updateChart with composable usage
    const updateChart = (timestamps, series, isInterpolatedIndoor, isInterpolatedOutdoor, gapMetadata) => {
      // Remove chart.clear(); -- not needed with composable pattern

      // Function to determine if a timestamp is within a "big" gap period
      const isInBigGap = (timestamp, gapPeriods, thresholdMinutes) => {
        return gapPeriods.some(period => {
          const start = new Date(period.start).getTime();
          const end = new Date(period.end).getTime();
          const ts = new Date(timestamp).getTime();
          return ts >= start && ts <= end && period.duration_minutes >= thresholdMinutes;
        });
      };

      // Enhance series to visually indicate gaps using markers and break lines for large gaps
      const modifiedSeries = series.map(s => {
        if (s.name.includes('indoor') && isInterpolatedIndoor && gapMetadata) {
          const indoorGapPeriods = gapMetadata.indoor_gap_periods || [];
          const dataWithStyles = s.data.map((value, index) => {
            const timestamp = timestamps[index];
            const isBigGap = isInBigGap(timestamp, indoorGapPeriods, gapThreshold.value);
            const isLargeGap = isInBigGap(timestamp, indoorGapPeriods, largeGapThreshold.value);
            const isInterpolated = isInterpolatedIndoor[index];
            const showMarker = isBigGap || (showAllInterpolated.value && isInterpolated);
            return {
              value: isLargeGap ? null : value, // Break the line for large gaps
              symbol: showMarker ? 'circle' : 'none',
              symbolSize: showMarker ? 6 : 0,
              itemStyle: showMarker ? { color: 'red' } : {}
            };
          });
          return { ...s, data: dataWithStyles, connectNulls: false }; // Ensure lines are broken at null values
        } else if (s.name.includes('outdoor') && isInterpolatedOutdoor && gapMetadata) {
          const outdoorGapPeriods = gapMetadata.outdoor_gap_periods || [];
          const dataWithStyles = s.data.map((value, index) => {
            const timestamp = timestamps[index];
            const isBigGap = isInBigGap(timestamp, outdoorGapPeriods, gapThreshold.value);
            const isLargeGap = isInBigGap(timestamp, outdoorGapPeriods, largeGapThreshold.value);
            const isInterpolated = isInterpolatedOutdoor[index];
            const showMarker = isBigGap || (showAllInterpolated.value && isInterpolated);
            return {
              value: isLargeGap ? null : value, // Break the line for large gaps
              symbol: showMarker ? 'circle' : 'none',
              symbolSize: showMarker ? 6 : 0,
              itemStyle: showMarker ? { color: 'red' } : {}
            };
          });
          return { ...s, data: dataWithStyles, connectNulls: false }; // Ensure lines are broken at null values
        } else if (s.name.includes('delta') && isInterpolatedIndoor && isInterpolatedOutdoor && gapMetadata) {
          const indoorGapPeriods = gapMetadata.indoor_gap_periods || [];
          const outdoorGapPeriods = gapMetadata.outdoor_gap_periods || [];
          const dataWithStyles = s.data.map((value, index) => {
            const timestamp = timestamps[index];
            const isBigGapIndoor = isInBigGap(timestamp, indoorGapPeriods, gapThreshold.value);
            const isBigGapOutdoor = isInBigGap(timestamp, outdoorGapPeriods, gapThreshold.value);
            const isLargeGapIndoor = isInBigGap(timestamp, indoorGapPeriods, largeGapThreshold.value);
            const isLargeGapOutdoor = isInBigGap(timestamp, outdoorGapPeriods, largeGapThreshold.value);
            const isInterpolated = isInterpolatedIndoor[index] || isInterpolatedOutdoor[index];
            const isBigGap = isBigGapIndoor || isBigGapOutdoor;
            const isLargeGap = isLargeGapIndoor || isLargeGapOutdoor;
            const showMarker = isBigGap || (showAllInterpolated.value && isInterpolated);
            return {
              value: isLargeGap ? null : value, // Break the line for large gaps
              symbol: showMarker ? 'circle' : 'none',
              symbolSize: showMarker ? 6 : 0,
              itemStyle: showMarker ? { color: 'red' } : {}
            };
          });
          return { ...s, data: dataWithStyles, connectNulls: false }; // Ensure lines are broken at null values
        }
        return s;
      });

      // Add shaded regions for gap periods, with stronger shading for large gaps
      const markAreas = [];
      if (gapMetadata) {
        const indoorGapPeriods = gapMetadata.indoor_gap_periods || [];
        const outdoorGapPeriods = gapMetadata.outdoor_gap_periods || [];
        const allGapPeriods = [...indoorGapPeriods, ...outdoorGapPeriods];

        allGapPeriods.forEach(period => {
          const isLargeGap = period.duration_minutes >= largeGapThreshold.value;
          markAreas.push({
            name: isLargeGap ? 'Large Gap' : 'Gap Period',
            xAxis: period.start,
            itemStyle: { color: isLargeGap ? 'rgba(255, 0, 0, 0.3)' : 'rgba(255, 0, 0, 0.1)' } // Stronger shading for large gaps
          }, {
            xAxis: period.end
          });
        });
      }

      // Georgia Tech Gold for tooltip marker
      const gtGold = '#B3A369';
      // Navy for text contrast
      const gtNavy = '#003057';

      const option = {
        title: {
          text: getChartTitle(),
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 18,
            fontWeight: 600
          }
        },
        legend: {
          top: 'bottom',
          textStyle: { fontWeight: 'bold' },
          data: series.map(s => s.name),
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: gtNavy,
          borderColor: gtGold,
          textStyle: {
            color: '#fff'
          },
          extraCssText: 'box-shadow: 0 2px 12px rgba(0,0,0,0.2);',
          axisPointer: {
            lineStyle: { color: gtGold },
            crossStyle: { color: gtGold }
          },
          formatter: (params) => {
            const index = params[0].dataIndex;
            let tooltipText = `<b style="color:${gtGold}">${params[0].axisValueLabel}</b><br/>`;
            params.forEach(p => {
              let interpolationStatus = '';
              if (p.seriesName.includes('indoor') && isInterpolatedIndoor && isInterpolatedIndoor[index]) {
                interpolationStatus = ` <span style="color:${gtGold}">(Indoor Interpolated)</span>`;
              } else if (p.seriesName.includes('outdoor') && isInterpolatedOutdoor && isInterpolatedOutdoor[index]) {
                interpolationStatus = ` <span style="color:${gtGold}">(Outdoor Interpolated)</span>`;
              } else if (p.seriesName.includes('delta')) {
                if (isInterpolatedIndoor && isInterpolatedIndoor[index] && isInterpolatedOutdoor && isInterpolatedOutdoor[index]) {
                  interpolationStatus = ` <span style="color:${gtGold}">(Both Interpolated)</span>`;
                } else if (isInterpolatedIndoor && isInterpolatedIndoor[index]) {
                  interpolationStatus = ` <span style="color:${gtGold}">(Indoor Interpolated)</span>`;
                } else if (isInterpolatedOutdoor && isInterpolatedOutdoor[index]) {
                  interpolationStatus = ` <span style="color:${gtGold}">(Outdoor Interpolated)</span>`;
                }
              }
              tooltipText += `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:10px;height:10px;background:${gtGold};"></span><b>${p.seriesName}:</b> <span style="color:#fff">${p.value !== null ? p.value : 'N/A'}</span>${interpolationStatus}<br/>`;
            });
            return tooltipText;
          },
        },
        xAxis: {
          type: 'category',
          data: timestamps || [],
          name: 'Time (UTC)',
          nameLocation: 'center',
          nameTextStyle: {
            padding: [35, 0, 0, 0],
            fontWeight: 600,
            fontSize: 14
          },
        },
        yAxis: {
          type: 'value',
          name: getYAxisLabel(),
          nameLocation: 'center',
          nameTextStyle: {
            padding: [0, 0, 40, 0],
            fontWeight: 600,
            fontSize: 14
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: 80,
          containLabel: true,
        },
        dataZoom: [
          {
            type: 'inside',
          },
          {
            type: 'slider',
            bottom: 40,
          },
        ],
        series: modifiedSeries,
        markArea: {
          data: markAreas
        },
        animationDuration: 1500 // Add smooth animation
      };

      setChartOption(option);
    };

    // Watch for filter changes to update chart title and axis labels responsively
    watch([selectedLocation, selectedSensor, selectedIndoorSensor, selectedOutdoorSensor, showDelta], () => {
      const chart = getChartInstance();
      if (chart && typeof chart.resize === 'function') chart.resize();
    });

    // Ensure chart resizes on sidebar toggle and window resize
    watch(sidebarOpen, () => {
      const chart = getChartInstance();
      if (chart && typeof chart.resize === 'function') chart.resize();
    });

    // Add window resize event for chart resizing
    onMounted(async () => {
      try {
        const response = await axios.get('/assets/data/locations.json');
        locationSensorMap.value = response.data;
        locations.value = Object.keys(locationSensorMap.value);

        if (!selectedLocation.value) {
          selectedLocation.value = locations.value[0];
        }

        updateSensorOptions();
        initChartWrapper();
        fetchData();
      } catch (error) {
        console.error('Error fetching location data:', error);
      }

      // After fetchData and chart init, force resize
      setTimeout(() => {
        const chart = getChartInstance();
        if (chart && typeof chart.resize === 'function') chart.resize();
      }, 300);

      window.addEventListener('resize', () => {
        const chart = getChartInstance();
        if (chart && typeof chart.resize === 'function') chart.resize();
      });

      // --- Add this block for fullscreen resize support ---
      const resizeOnFullscreen = () => {
        const chart = getChartInstance();
        if (chart && typeof chart.resize === 'function') chart.resize();
      };
      document.addEventListener('fullscreenchange', resizeOnFullscreen);
      document.addEventListener('webkitfullscreenchange', resizeOnFullscreen);
      document.addEventListener('mozfullscreenchange', resizeOnFullscreen);
      document.addEventListener('MSFullscreenChange', resizeOnFullscreen);
      // ---------------------------------------------------
    });

    const updateSensorsFromParent = (location, indoor, outdoor) => {
      selectedLocation.value = location;
      selectedIndoorSensor.value = indoor;
      selectedOutdoorSensor.value = outdoor;
      updateSensorOptions();
      fetchData();
    };

    // Responsive sidebar: close on small screens, open on large
    const handleResize = () => {
      sidebarOpen.value = window.innerWidth > 900;
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    // Chart export as PNG
    const exportChart = () => {
      const chart = getChartInstance();
      if (chart) {
        const url = chart.getDataURL({ type: 'png', pixelRatio: 2 });
        const link = document.createElement('a');
        link.href = url;
        link.download = `${getChartTitle()}.png`;
        link.click();
      }
    };

    // Fullscreen chart
    const fullscreenChart = () => {
      const chartContainer = document.getElementById('chart');
      const mainContent = chartContainer?.closest('.main-content');
      if (!chartContainer || !mainContent) return;
      // Add fullscreen-mode class to main-content for fullscreen styles
      mainContent.classList.add('fullscreen-mode');
      // Remove the class when exiting fullscreen
      const removeClass = () => mainContent.classList.remove('fullscreen-mode');
      document.addEventListener('fullscreenchange', removeClass, { once: true });
      document.addEventListener('webkitfullscreenchange', removeClass, { once: true });
      document.addEventListener('mozfullscreenchange', removeClass, { once: true });
      document.addEventListener('MSFullscreenChange', removeClass, { once: true });

      if (chartContainer.requestFullscreen) chartContainer.requestFullscreen();
      else if (chartContainer.webkitRequestFullscreen) chartContainer.webkitRequestFullscreen();
      else if (chartContainer.mozRequestFullScreen) chartContainer.mozRequestFullScreen();
      else if (chartContainer.msRequestFullscreen) chartContainer.msRequestFullscreen();
    };

    return {
      locations,
      sensors,
      indoorSensorOptions,
      outdoorSensorOptions,
      selectedLocation,
      selectedSensor,
      selectedIndoorSensor,
      selectedOutdoorSensor,
      showDelta,
      showAllInterpolated, // Expose new state to template
      possibleRanges,
      selectedRangeDays,
      gapWarning,
      gapThreshold, // Expose new state to template
      gapThresholdOptions, // Expose gap threshold options to template
      largeGapThreshold, // Expose new state for large gap threshold
      updateSensorOptions,
      fetchData,
      updateSensorsFromParent,
      sidebarOpen,
      loading,
      exportChart,
      fullscreenChart,
      getChartTitle,
      // Replace initChart with initChartWrapper
      initChart: initChartWrapper,
    };
  },
};
</script>

<style scoped>
/* === Layout Container === */
.container {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 100vh;
  max-width: 100vw;
  margin: 0 auto;
  background: linear-gradient(135deg, #f9f9f9, #e9ecef);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  padding: 0;
}

/* === Sidebar Styles === */
.sidebar {
  position: relative;
  width: 18vw;
  min-width: 220px;
  max-width: 320px;
  background: #fff;
  border-radius: 12px 0 0 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);
  transition: width 0.3s, left 0.3s, box-shadow 0.3s;
  padding: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  overflow-y: auto;
}

.sidebar .sidebar-toggle {
  position: absolute;
  top: 12px;
  left: 12px;
  background: none;
  border: none;
  font-size: 1.7rem;
  cursor: pointer;
  z-index: 20;
  color: #007bff;
  padding: 0 8px;
  transition: color 0.2s, background 0.2s;
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar .sidebar-toggle:hover,
.sidebar .sidebar-toggle:focus {
  background: #B3A36920;
  outline: 2px solid #B3A369;
}

.sidebar:not(.open) {
  width: 56px !important;
  min-width: 56px !important;
  max-width: 56px !important;
  box-shadow: none !important;
  background: transparent;
  margin-right: 0;
  overflow: hidden;
}

.sidebar:not(.open) .sidebar-toggle {
  left: 8px;
}

.sidebar-content {
  padding: 16px 20px;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  box-sizing: border-box;
}

.sidebar:not(.open) .sidebar-content {
  display: none;
}

/* === Sidebar Form Controls === */
.selectors {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.selector-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 15px;
  min-height: 50px;
}

label {
  font-size: 14px;
  font-weight: 600;
  color: #34495e;
}

select {
  padding: 10px 15px;
  font-size: 14px;
  border: 1px solid #dfe6e9;
  border-radius: 8px;
  background-color: #fff;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg fill="none" stroke="%2334495e" stroke-width="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"></path></svg>');
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  width: 100%;
}

select:hover,
select:focus {
  border-color: #007bff;
  box-shadow: 0 0 8px rgba(0, 123, 255, 0.3);
  outline: none;
}

input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
  width: 18px;
  height: 18px;
  accent-color: #007bff;
  transition: transform 0.2s ease;
}

input[type="checkbox"]:hover {
  transform: scale(1.1);
}

.checkbox-item>label {
  font-size: 14px;
  color: #34495e;
  cursor: pointer;
  transition: color 0.3s ease;
  font-weight: normal;
}

input[type="checkbox"]:hover+label,
input[type="checkbox"]:checked+label {
  color: #007bff;
}

/* === Main Content & Chart Area === */
.main-content {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  box-sizing: border-box;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.main-content.expanded {
  padding-left: 8px;
  padding-right: 8px;
}

.chart-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 12px;
  z-index: 2;
  background: transparent;
}

.chart-actions button {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: 8px;
}

.chart-actions button:hover {
  background: #667eea;
  color: #fff;
}

#chart {
  flex: 1 1 0%;
  width: 100% !important;
  height: 100% !important;
  min-width: 0;
  min-height: 300px;
  max-height: 100%;
  background: #fff;
  /* Set background to white */
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin: 0;
  padding: 0;
  overflow: hidden;
  z-index: 1;
}

/* Transparent background and no shadow for chart in fullscreen mode */
.fullscreen-mode #chart {
  background: transparent !important;
  box-shadow: none !important;
}

/* Optional: dark theme support for fullscreen */
@media (prefers-color-scheme: dark) {
  .fullscreen-mode #chart {
    background: transparent !important;
    box-shadow: none !important;
  }
}

/* === Warning & Loading Spinner === */
.warning {
  color: #e67e22;
  background-color: #fff3e6;
  padding: 12px 20px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-in-out;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40px;
  margin-bottom: 10px;
}

.spinner {
  border: 4px solid #e3e3e3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Responsive Adjustments === */
@media (max-width: 1200px) {
  .main-content {
    max-width: 100vw;
    padding: 16px 4px 16px 4px;
  }

  .main-content.expanded {
    padding-left: 4px;
    padding-right: 4px;
  }
}

@media (max-width: 900px) {
  .container {
    flex-direction: column;
    padding: 0;
    height: 100vh;
  }

  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    width: 80vw;
    min-width: 0;
    max-width: 100vw;
    height: 100vh;
    margin: 0;
    border-radius: 0 12px 12px 0;
    box-shadow: 2px 0 16px rgba(0, 0, 0, 0.18);
    background: #fff;
    z-index: 100;
    transition: left 0.3s;
    overflow: hidden;
    padding: 8vw 1vw 1vw 1vw;
  }

  .main-content,
  .main-content.expanded {
    max-width: 100vw;
    width: 100vw;
    padding: 2vw 1vw 1vw 1vw;
    height: 100vh;
  }

  #chart {
    height: 50vh;
    min-height: 0;
  }
}

@media (max-width: 600px) {
  .sidebar {
    width: 100vw;
    min-width: 0;
    max-width: 100vw;
    border-radius: 0;
  }

  .sidebar-content {
    padding: 40px 6px 6px 6px;
  }

  .main-content,
  .main-content.expanded {
    padding: 1vw 1vw 1vw 1vw;
    height: 100vh;
  }

  #chart {
    height: 40vh;
    min-height: 0;
  }
}

/* === Dark Theme === */
@media (prefers-color-scheme: dark) {
  .container {
    background: linear-gradient(135deg, #1a1a1a, #2c3e50);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  }

  .selectors {
    background: #2c3e50;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  }

  label,
  .checkbox-item>label {
    color: #ccc;
  }

  select {
    background-color: #34495e;
    color: #fff;
    border-color: #555;
    background-image: url('data:image/svg+xml;utf8,<svg fill="none" stroke="%23ccc" stroke-width="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"></path></svg>');
  }

  select:hover,
  select:focus {
    border-color: #007bff;
    box-shadow: 0 0 8px rgba(0, 123, 255, 0.5);
  }

  .warning {
    color: #e67e22;
    background-color: #3e2723;
  }

  #chart {
    background-color: #2c3e50;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  }
}

/* === Hide Unused Elements === */
.filter-summary,
.chart-title {
  display: none !important;
}
</style>
